import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import BookingCharges from '../../Bookings/Fees/BookingCharges';
import {
    buildOptionalServiceExpenseLines,
    currency,
} from './reservationEdit.utils';

type Props = {
    show: boolean;
    fees: any;
    isCalculating: boolean;
    showSkeleton: boolean;
    isFirstRender: boolean;
    reservationDetails: any;
    module: any;
    /** Live `jobs` from react-hook-form so catering/massage line totals appear in the sidebar. */
    formJobs?: unknown;
};

export default function ReservationChargesCard({
    show,
    fees,
    isCalculating,
    showSkeleton,
    isFirstRender,
    reservationDetails,
    module,
    formJobs,
}: Props) {
    const theme = useTheme();
    const [chargesExpanded, setChargesExpanded] = useState(false);

    const loading = (isCalculating || showSkeleton) && isFirstRender;

    const mergedExpenses = useMemo(() => {
        const api = reservationDetails?.expenses ?? [];
        const optional = buildOptionalServiceExpenseLines(formJobs);
        return [...optional, ...api];
    }, [reservationDetails?.expenses, formJobs]);

    const totalExpenses = useMemo(() => {
        return mergedExpenses.reduce(
            (sum: number, item: any) => sum + (Number(item?.amount) || 0),
            0,
        );
    }, [mergedExpenses]);

    const bookingTotal =
        (fees?.bookingTotal?.total ??
            reservationDetails?.bookingTotal?.total) ||
        0;

    const overallTotal = useMemo(() => {
        return bookingTotal + totalExpenses;
    }, [bookingTotal, totalExpenses]);

    if (!show) return null;

    return (
        <Box sx={{ width: '100%' }}>
            {loading ? (
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                </Box>
            ) : fees || reservationDetails ? (
                <Accordion
                    expanded={chargesExpanded}
                    onChange={(_, next) => setChargesExpanded(next)}
                    disableGutters
                    elevation={0}
                    sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: 0,
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        overflow: 'visible',
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 22,
                                }}
                            />
                        }
                        sx={{
                            px: 0,
                            py: 0,
                            minHeight: 44,
                            borderRadius: 1,
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.04,
                                ),
                            },
                            '& .MuiAccordionSummary-content': {
                                my: 0,
                                alignItems: 'center',
                                flexGrow: 1,
                                marginRight: 0.5,
                            },
                            '& .MuiAccordionSummary-expandIconWrapper': {
                                marginLeft: 0,
                            },
                        }}
                    >
                        <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={1}
                            sx={{ width: '100%', pr: 0.5 }}
                        >
                            <Typography
                                component='span'
                                sx={{
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    fontSize: 11,
                                    letterSpacing: 0.9,
                                }}
                            >
                                Total charges
                            </Typography>
                            <Typography
                                component='span'
                                sx={{
                                    fontWeight: 800,

                                    letterSpacing: -0.02,
                                }}
                            >
                                {currency(overallTotal)}
                            </Typography>
                        </Stack>
                    </AccordionSummary>

                    <AccordionDetails
                        sx={{
                            pt: 1.5,
                            px: 0,
                            pb: 0,
                        }}
                    >
                        <BookingCharges
                            charges={
                                fees?.charges ??
                                reservationDetails?.charges ??
                                []
                            }
                            bookingTotal={
                                fees?.bookingTotal ??
                                reservationDetails?.bookingTotal
                            }
                            expenses={mergedExpenses}
                            module={module}
                            billingExcluded={
                                reservationDetails?.billingExcluded ?? []
                            }
                            manualDiscount={
                                !!reservationDetails?.manualDiscount
                            }
                            isEditing
                        />
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </Box>
    );
}
